import { useState, useEffect } from 'react';
import { rolesService } from '../services/rolesService';
import { Permission, UserWithRole } from '../types/roles';
import authService from '../services/authService';

export function usePermissions() {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [user, setUser] = useState<UserWithRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      const session = authService.getCurrentSession();
      if (!session?.user) {
        setPermissions([]);
        setUser(null);
        return;
      }

      const userWithRole = await rolesService.getUserWithRole(session.user.id);
      setUser(userWithRole);

      const userPermissions = await rolesService.getUserPermissions(session.user.id);
      setPermissions(userPermissions);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError('Failed to load permissions');
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (module: string, action: string): boolean => {
    return rolesService.hasPermission(permissions, module, action);
  };

  const hasAnyPermission = (checks: Array<{ module: string; action: string }>): boolean => {
    return rolesService.hasAnyPermission(permissions, checks);
  };

  const hasAllPermissions = (checks: Array<{ module: string; action: string }>): boolean => {
    return rolesService.hasAllPermissions(permissions, checks);
  };

  const canView = (module: string): boolean => {
    return hasPermission(module, 'view');
  };

  const canCreate = (module: string): boolean => {
    return hasPermission(module, 'create');
  };

  const canEdit = (module: string): boolean => {
    return hasPermission(module, 'edit');
  };

  const canDelete = (module: string): boolean => {
    return hasPermission(module, 'delete');
  };

  const canManage = (module: string): boolean => {
    return hasPermission(module, 'manage');
  };

  const canExport = (module: string): boolean => {
    return hasPermission(module, 'export');
  };

  const isAdmin = (): boolean => {
    return user?.user_role?.name === 'superadmin' || user?.user_role?.name === 'admin';
  };

  const isSuperAdmin = (): boolean => {
    return user?.user_role?.name === 'superadmin';
  };

  const isCollaborator = (): boolean => {
    return (
      user?.user_role?.name === 'colaborador_programador' ||
      user?.user_role?.name === 'colaborador_comercial'
    );
  };

  const isCollaboradorProgramador = (): boolean => {
    return user?.user_role?.name === 'colaborador_programador';
  };

  const isCollaboradorComercial = (): boolean => {
    return user?.user_role?.name === 'colaborador_comercial';
  };

  return {
    permissions,
    user,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canManage,
    canExport,
    isAdmin,
    isSuperAdmin,
    isCollaborator,
    isCollaboradorProgramador,
    isCollaboradorComercial,
    reload: loadPermissions
  };
}

export function useRoleCheck(requiredModule: string, requiredAction: string) {
  const { hasPermission, loading } = usePermissions();

  return {
    allowed: hasPermission(requiredModule, requiredAction),
    loading
  };
}

export function useAdminCheck() {
  const { isAdmin, loading } = usePermissions();

  return {
    isAdmin: isAdmin(),
    loading
  };
}
